import { trigger } from '@angular/animations';
import { DOCUMENT, isPlatformServer, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ComponentRef,
  ElementRef,
  HostListener,
  Inject,
  PLATFORM_ID,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { fadeDownIf } from '../../animations/fade-down.if.animation';
import { fadeScaleIf } from '../../animations/fade-scale.if.animation';
import { fadeToIf } from '../../animations/fade-to.if.animation';
import { fadeUpIf } from '../../animations/fade-up.if.animation';
import { ScreenSizes } from '../../classes/screen-sizes.class';
import { DialogOptions, DialogsComponentOpenData } from '../../interfaces/dialog-data.interface';
import { debounce } from '../../lib/debounce';
import { getScrollbarWidth } from '../../lib/get-scrollbar-width';
import { sleep } from '../../lib/sleep';
import { disableBodyScroll, enableBodyScroll } from '../../lib/toggle-body-scroll';
import { AnalyticsService } from '../../services/analytics.service';
import { DialogService } from '../../services/dialog.service';
import { SvgComponent } from '../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../shared/directives/button.directive';
import { ResizeDirective } from '../../shared/directives/resize.directive';

@Component({
  selector: 'app-dialogs',
  templateUrl: './dialogs.component.html',
  styleUrls: ['./dialogs.component.scss'],
  animations: [
    trigger('dialogBg', fadeToIf(0.25)),
    trigger('dialogContent', fadeScaleIf),
    trigger('dialogContentMobile', fadeUpIf),
    trigger('dialogContentMobileTop', fadeDownIf),
  ],
  standalone: true,
  imports: [ResizeDirective, ButtonDirective, SvgComponent, NgTemplateOutlet],
})
export class DialogsComponent {
  @ViewChild('placeholder', { read: ViewContainerRef, static: false })
  private viewRef: ViewContainerRef;
  componentRef: ComponentRef<{ dialogName: string; onGetData: (data: unknown, close: unknown) => unknown }>;
  dialogOpen = false;
  options: DialogOptions = {};
  data = {};
  openDialogResolve: (...args) => void;
  currentSize: string;
  currentSelector: string;
  component: DialogsComponentOpenData['component'];
  maxHeight: number;

  @HostListener('window:resize', ['$event']) winResize() {
    if (!this.dialogOpen) return;
    const size = ScreenSizes.screenSize();
    if (this.currentSize && size !== this.currentSize) this.setComponentDebounce();
    this.currentSize = size;
  }
  @HostListener('window:keyup', ['$event']) keyEvent(event: KeyboardEvent) {
    if (event.target['form']) return;
    if (event.key === 'Escape') this.close(undefined);
  }

  @HostListener('window:resize', ['$event']) resizeEvent() {
    this.maxHeight = window.innerHeight - 72;
  }

  constructor(
    private dialog: DialogService,
    private analytics: AnalyticsService,
    private elRef: ElementRef,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    if (isPlatformServer(this.platformId)) return;
    this.maxHeight = window.innerHeight - 72;
    this.dialog.onDialogOpen.subscribe(async (params: DialogsComponentOpenData) => {
      if (params?.options.layout !== 'popup') return;
      if (this.openDialogResolve) await this.close(undefined);
      this.dialogOpen = true;
      this.data = params.data;
      this.options = params.options;
      this.component = params.component;
      disableBodyScroll('dialog'); // duplicate because context menu enables it on mobile
      this.document.body.style.setProperty('--scrollbar-width', getScrollbarWidth() + 'px');
      await sleep(0);
      if (!this.viewRef) await sleep(100);
      if (!this.viewRef) return console.warn('viewRef not defined');
      disableBodyScroll('dialog'); // duplicate because context menu enables it on mobile
      this.document.body.style.setProperty('--scrollbar-width', getScrollbarWidth() + 'px');
      this.setComponent();
      this.currentSelector = this.componentRef?.instance?.dialogName;
      if (!this.currentSelector) console.warn('Dialog has no name', this.componentRef?.componentType?.name);
      this.analytics.track('open_dialog', false, { name: this.currentSelector });
      this.openDialogResolve = params.resolve;
    });
    this.dialog.onDialogClose.subscribe(e => {
      if (!e) return;
      this.close(false);
    });
  }

  private setComponentDebounce = debounce(() => {
    if (!this.dialogOpen) return;
    this.setComponent();
  }, 100);

  private async setComponent() {
    if (!this.viewRef) return;
    this.viewRef.clear();
    this.componentRef = this.viewRef.createComponent(this.component);
    this.componentRef.instance.onGetData?.(this.data, this.close.bind(this));
    await sleep(0);
    const input: HTMLInputElement = this.elRef.nativeElement.querySelector('input');
    if (input) input.focus();
  }

  async close(event: unknown, delay = 200) {
    if (!this.dialogOpen) return;
    this.analytics.track('close_dialog', false, { name: this.currentSelector });
    this.openDialogResolve?.(event);
    this.openDialogResolve = undefined;
    this.dialogOpen = false;
    const ref = this.componentRef;

    this.dialog.close(); // mark the dialog as closed

    // On !mobile we need to wait for the animation to finish before re-enabling the body scroll
    if (ScreenSizes.screenSize() !== 'mobile') await sleep(delay);
    enableBodyScroll('dialog');
    this.document.body.style.setProperty('--scrollbar-width', '0px');
    if (ScreenSizes.screenSize() === 'mobile') await sleep(delay);

    if (ref !== this.componentRef) return;
    this.componentRef?.destroy();
    this.componentRef = undefined;
  }
}
